import { Model } from '@k3/core/dist/src/lib/types/model.interface';
import { Addition, Base, Geometry } from '@react-three/csg';
import { memo, useMemo } from 'react';
import {
  BufferGeometry,
  ExtrudeGeometry,
  ExtrudeGeometryOptions,
  Matrix4,
  MeshStandardMaterial,
  RepeatWrapping,
  Shape,
  Texture,
  Vector2,
  Vector3,
  Vector3Tuple,
} from 'three';
import BeveledPlate from '../threejs/BeveledPlate';

export const getTableTopSubmodelKeys = (model: Model): string[] => {
  const props = model?.props as any;
  return props.subtractionModelKeys?.filter((key: string) => !!key) ?? [];
};

export interface TabletopProps {
  type?: 'rectangle' | 'rectangle-with-corner' | 'mdf-board';
  position?: Vector3Tuple;
  rotation?: Vector3Tuple;
  scale?: Vector3Tuple;
  width: number;
  height?: number;
  depth: number;
  materials?: {
    top: MeshStandardMaterial;
    bottom: MeshStandardMaterial;
    sides: MeshStandardMaterial;
  };
  segments?: number;
  radius?: number;
  cornerHeight?: number;
  debug?: boolean;
  mdfEdgeType?: 'straight' | 'oblique';
  mdfCornerRadius?: number;
  subtractions?: React.FC[];
}

const defaultMaterials = {
  top: new MeshStandardMaterial({ color: 'red' }),
  bottom: new MeshStandardMaterial({ color: 'green' }),
  sides: new MeshStandardMaterial({ color: 'blue' }),
};

const setupMap = (map: Texture, width: number, depth: number) => {
  const newMap = map.clone();

  newMap.repeat.set(newMap.repeat.x * width, newMap.repeat.y * depth);
  newMap.wrapS = newMap.wrapT = RepeatWrapping;

  return newMap;
};

const mapKeys = [
  'map',
  'roughnessMap',
  'normalMap',
  'metalnessMap',
  'aoMap',
] as const;

function setupMaterial(
  material: MeshStandardMaterial,
  lengthX: number,
  lengthY: number,
  debugColorMap?: Texture
) {
  const newMaterial = material.clone();

  mapKeys.forEach((key) => {
    if (newMaterial[key]) {
      newMaterial[key] = setupMap(
        newMaterial[key] as Texture,
        lengthX,
        lengthY
      );
    }
  });

  if (debugColorMap) {
    newMaterial.map = setupMap(debugColorMap, lengthX, lengthY);
  }

  return newMaterial;
}

const addRadiusPoints = (points: Vector2[], radius: number) => {
  radius /= 2;
  return points.reduce((acc, point, index, array) => {
    const prevIndex = index - 1 < 0 ? array.length - 1 : index - 1;
    const nextIndex = (index + 1) % array.length;

    const directionBackward = array[prevIndex].clone().sub(point).normalize();
    const directionForward = array[nextIndex].clone().sub(point).normalize();

    acc.push(
      point.clone().add(directionBackward.clone().multiplyScalar(radius))
    );
    acc.push(point);
    acc.push(
      point.clone().add(directionForward.clone().multiplyScalar(radius))
    );

    return acc;
  }, [] as Vector2[]);
};

function createRoundedShapeFromPoints(points: Vector2[], radius: number) {
  const roundedPoints = addRadiusPoints(points, radius);
  const ctx = new Shape();

  roundedPoints.forEach((point, index) => {
    if (index === 0) {
      ctx.moveTo(point.x, point.y);
      return;
    }

    if ((index - 1) % 3 === 0) {
      ctx.quadraticCurveTo(
        point.x,
        point.y,
        roundedPoints[index + 1].x,
        roundedPoints[index + 1].y
      );
      return;
    }
    ctx.lineTo(point.x, point.y);
  });

  return ctx;
}

function createRectangleWithCorner(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  corner: number
) {
  const completeWidth = width + corner;
  const completeHeight = height + corner;

  const points = [
    new Vector2(x, y),
    new Vector2(x, y + completeHeight),
    new Vector2(x + width, y + completeHeight),
    new Vector2(x + completeWidth, y + height),
    new Vector2(x + completeWidth, y),
  ];

  return createRoundedShapeFromPoints(points, radius);
}

function createRectangle(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const points = [
    new Vector2(x, y),
    new Vector2(x, y + height),
    new Vector2(x + width, y + height),
    new Vector2(x + width, y),
  ];

  return createRoundedShapeFromPoints(points, radius);
}

export const DummySubtraction = () => {
  return (
    <Addition>
      <boxGeometry args={[0, 0, 0]} />
    </Addition>
  );
};

const defaultSubtractions = [DummySubtraction];

const rotateAroundX90: Vector3Tuple = [Math.PI / 2, 0, 0];

// eslint-disable-next-line react/display-name
export const Tabletop = memo(
  ({
    type,
    position,
    rotation,
    scale,
    debug = false,
    width,
    height = 0.025,
    depth,
    materials = defaultMaterials,
    segments = 6,
    radius = 0.001,
    cornerHeight = 0.2,
    subtractions = defaultSubtractions,
    mdfEdgeType = 'straight',
    mdfCornerRadius = 0,
  }: TabletopProps) => {
    const realMaterials = useMemo(
      () => ({
        top: setupMaterial(materials.top, 1, 1),
        side: setupMaterial(materials.sides, 1, 1),
      }),
      [materials, width, depth, height]
    );

    const extrudedGeometry = useMemo(() => {
      let geometry = new BufferGeometry();

      if (type === 'mdf-board') {
        const beveledPlate = new BeveledPlate({
          cornerRadius: mdfCornerRadius / 1000,
          edgeRadius: 0.001,
          topOffset: mdfEdgeType === 'oblique' ? 0.005 : 0,
          edgeAngle: mdfEdgeType === 'oblique' ? 45 : undefined,
          size: new Vector3(width, height, depth),
        });
        geometry = beveledPlate.getGeometry();
      } else {
        let shape: Shape | undefined;

        if (type === 'rectangle') {
          shape = createRectangle(-width / 2, -depth / 2, width, depth, radius);
        } else {
          shape = createRectangleWithCorner(
            -width / 2,
            -depth / 2,
            width,
            depth,
            radius,
            cornerHeight
          );
        }

        const extrudeSettings: ExtrudeGeometryOptions = {
          depth: height - radius * 2,
          bevelEnabled: true,
          bevelSegments: segments,
          steps: 2,
          bevelSize: radius,
          bevelThickness: radius,
        };

        geometry = new ExtrudeGeometry(shape, extrudeSettings);
        geometry.applyMatrix4(new Matrix4().makeTranslation(0, 0, -height / 2));
      }

      return geometry;
    }, [
      radius,
      width,
      depth,
      height,
      segments,
      cornerHeight,
      type,
      mdfCornerRadius,
      mdfEdgeType,
    ]);

    const subtractionComponents = subtractions.map(
      (SubtractionComponent, i) => <SubtractionComponent key={i} />
    );

    const tableMaterials = useMemo(
      () => [realMaterials.top, realMaterials.side],
      [realMaterials]
    );

    return (
      <group
        name="tabletop"
        position={position}
        rotation={rotation}
        scale={scale}
      >
        <mesh>
          <Geometry useGroups>
            <Base geometry={extrudedGeometry} rotation={rotateAroundX90}>
              <primitive attach="material" object={tableMaterials} />
            </Base>

            {subtractionComponents.length > 0 ? subtractionComponents : null}
          </Geometry>
        </mesh>
      </group>
    );
  }
);
